// ============================================
// UserMenu — Avatar dropdown (profile + sign out)
// ============================================
import { useState, useRef, useEffect } from 'react'
import { useAuth } from '@/shared/hooks/useAuth'


function getInitials(name?: string | null): string {
  if (!name) return '?'
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function UserMenu() {
  const { user, signOut } = useAuth()
  const [open, setOpen] = useState(false)
  const [signingOut, setSigningOut] = useState(false)

  const ref = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handleSignOut = async () => {
    setSigningOut(true)
    try {
      await signOut()
      window.location.href = '/'
    } catch {
      setSigningOut(false)
    }
  }

  const initials = getInitials(user?.name)

  return (
    <div ref={ref} className="relative">
      {/* Avatar button */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="User menu"
        className="flex items-center gap-2 group cursor-pointer border-none bg-transparent p-0"
      >
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden
            border border-nw/30 group-hover:border-nw/60
            transition-all duration-300 group-hover:shadow-[0_0_16px_color-mix(in_oklch,var(--color-nw),transparent_75%)]"
        >
          {user?.image ? (
            <img
              src={user.image}
              alt={user.name ?? 'avatar'}
              className="w-full h-full object-cover"
              referrerPolicy="no-referrer"
            />
          ) : (
            <span className="text-[10px] font-light tracking-[0.05em] text-nw/80">
              {initials}
            </span>
          )}
        </div>
        {/* chevron */}
        <svg
          className={`w-3 h-3 text-white/30 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 12 12" fill="none" aria-hidden="true"
        >
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute right-0 top-full mt-3 w-56 z-50 rounded-xl overflow-hidden
            bg-[#0d1a2a]
            border border-white/10
            shadow-[0_8px_32px_rgba(0,0,0,0.4),0_2px_8px_rgba(0,0,0,0.2)]"
          style={{ animation: 'fadeIn 0.15s ease-out forwards' }}
        >
          {/* User info */}
          <div className="px-4 py-4 border-b border-nw/10">
            <p className="text-[12px] font-light tracking-[0.05em] text-white/80 truncate">
              {user?.name ?? '—'}
            </p>
            <p className="text-[10px] font-light mt-0.5 tracking-[0.03em] text-nw/40 truncate">
              {user?.email ?? ''}
            </p>
          </div>

          {/* Menu items */}
          <div className="py-1.5">

            <button
              onClick={handleSignOut}
              disabled={signingOut}
              className="flex items-center gap-3 w-full px-4 py-2.5 text-[11px] font-light
                tracking-[0.08em] uppercase text-white/40 bg-transparent border-none cursor-pointer
                hover:text-nw-red/80 hover:bg-nw-red/5 transition-colors duration-200
                disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <svg className="w-3.5 h-3.5 opacity-60" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M6 2H3a1 1 0 00-1 1v10a1 1 0 001 1h3M10 11l3-3-3-3M13 8H6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {signingOut ? 'Signing out…' : 'Sign out'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
